import { Link, useForm } from "@inertiajs/react";
import { Plus } from "lucide-react";
import Steps, { Step } from "@/components/help/Steps";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import StudentNameGuidance from "@/components/schools/StudentNameGuidance";

function NewClassroomFormPreview() {
  const { data, setData } = useForm({ name: "Room 12", grade: "Grade 5" });
  return (
    <Card>
      <CardHeader>
        <CardTitle>New classroom</CardTitle>
        <CardDescription>Name and grade are visible to families on take-home sheets.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-5">
          <FormField name="name" label="Name" required value={data.name} onChange={(e) => setData("name", e.target.value)} />
          <FormField name="grade" label="Grade" value={data.grade} onChange={(e) => setData("grade", e.target.value)} />
          <Button>Create classroom</Button>
        </form>
      </CardContent>
    </Card>
  );
}

function AddStudentFormPreview() {
  const { data, setData } = useForm({ first_name: "Sarah", last_name: "Thompson" });
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Add a student</p>
      <StudentNameGuidance id="preview-student-name-guidance" />
      <form className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end mt-3">
        <FormField name="first_name" label="Full first name" value={data.first_name} onChange={(e) => setData("first_name", e.target.value)} aria-describedby="preview-student-name-guidance" />
        <FormField name="last_name" label="Full last name" value={data.last_name} onChange={(e) => setData("last_name", e.target.value)} aria-describedby="preview-student-name-guidance" />
        <Button><Plus className="h-4 w-4" /> Add student</Button>
      </form>
    </div>
  );
}

export default function AddingClassroomsManually() {
  return (
    <>
      <p>
        For a small school, or when you're just adding a single student mid-campaign, doing it by hand is quicker than fiddling with a spreadsheet.
      </p>

      <h2>Adding a classroom</h2>
      <Steps>
        <Step title="Click Classrooms in your sidebar, then New classroom">
          <p>The "New classroom" button sits in the top-right of the classrooms list.</p>
        </Step>
        <Step title="Fill in the name and grade">
          <p>
            The name is what families will see on the take-home sheet and donation page — pick something they'd recognize. Grade is just a label.
          </p>
          <Screenshot caption="Adding a classroom">
            <div className="max-w-md mx-auto">
              <NewClassroomFormPreview />
            </div>
          </Screenshot>
        </Step>
      </Steps>

      <h2>Adding students one at a time</h2>
      <p>
        On the classroom page, scroll to the student list. Enter the student's full first and last name, then click <strong>Add student</strong>. PagePledge shortens the last name for privacy and adds a number if needed so you can tell students apart. It also creates their activation code and donation page. Even very short last names are never saved or shown in full. Once your campaign is paid for, you can't add students past your plan's limit; <Link href="/help/articles/paying-for-your-campaign">contact us to upgrade</Link>.
      </p>
      <Screenshot caption="Adding a student">
        <div className="max-w-xl mx-auto">
          <AddStudentFormPreview />
        </div>
      </Screenshot>
      <Callout variant="tip">
        <p>
          The form jumps back to the start after each student, so you can rattle through 20 names faster than you'd think.
        </p>
      </Callout>

      <h2>Editing or removing</h2>
      <p>
        Each student row has buttons to edit their name, swap their activation code (see <Link href="/help/articles/rotating-activation-codes">Replacing a lost take-home sheet</Link>), or remove them. You can remove students up until reading wraps up — after that, the roster's frozen.
      </p>
      <p>
        When changing a last name, enter the full new last name. PagePledge will shorten it and add a number if needed so you can tell students apart. Leave the last-name field blank to keep the current shortened name.
      </p>
      <Callout variant="warning" title="Don't remove students mid-campaign">
        <p>
          Removing wipes out their donation page and any minutes they've already logged. If a student moves to a different classroom, edit their existing record — don't delete and remake, or their parents' bookmarks won't work anymore.
        </p>
      </Callout>

      <p>
        Adding and removing students is something only school admins can do. Teachers can see their roster and log minutes, but they can't add or remove students.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/importing-roster-csv">Adding students from a spreadsheet</Link> — when you've got more than a handful.</li>
        <li><Link href="/help/articles/inviting-teachers">Inviting teachers</Link>.</li>
        <li><Link href="/help/articles/printing-take-home-sheets">Print take-home sheets</Link>.</li>
      </ul>
    </>
  );
}
